/**
 * FileListItem Component
 *
 * Reusable file list item component.
 * Displays a single file with optional preview, delete, download, and annexe checkbox.
 */

import React from 'react';
import { __ } from '../i18n/vsme';
import { deleteIconSm, downloadIconSm, viewIconSm } from '@tpct/icons';

// Types that VsmePdfMergerService can embed directly — anything else gets a placeholder page
const ANNEXE_SUPPORTED_MIME_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

type FileListItemFile = {
  id: number;
  file_name?: string;
  name?: string;
  mime_type?: string;
  attachToReport?: boolean;
};

type FileListItemProps<T extends FileListItemFile = FileListItemFile> = {
  file: T;
  onPreview?: (file: T) => void;
  onDelete?: (fileId: number) => void;
  onDownload?: (file: T) => void;
  onAttachToReportChange?: (fileId: number, checked: boolean) => void;
  isDeleting?: boolean;
  showPreview?: boolean;
  showDelete?: boolean;
  showDownload?: boolean;
  showAttachToReport?: boolean;
  /** Label of the delete button: "Retirer" where it only unlinks the file. */
  deleteLabel?: string;
  className?: string;
};

const FileListItem = <T extends FileListItemFile>({
  file,
  onPreview,
  onDelete,
  onDownload,
  onAttachToReportChange,
  isDeleting = false,
  showPreview = true,
  showDelete = true,
  showDownload = false,
  showAttachToReport = false,
  deleteLabel,
  className = '',
}: FileListItemProps<T>) => {
  const fileName = file.file_name ?? file.name ?? '';

  return (
    <li className={`file-item ${className}`}>
      <div className="file-item__info">
        <span className="file-name">{fileName}</span>
      </div>
      <div className="file-item__actions">
        {showAttachToReport && onAttachToReportChange && ANNEXE_SUPPORTED_MIME_TYPES.has(file.mime_type ?? '') && (
          <label className="file-item__annexe-label">
            <input
              type="checkbox"
              checked={file.attachToReport ?? false}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onAttachToReportChange(file.id, e.target.checked)
              }
              className="file-item__annexe-checkbox"
              aria-label={__('Joindre en annexe du rapport')}
            />
            <span className="file-item__annexe-text">{__('Annexe')}</span>
          </label>
        )}
        {showPreview && onPreview && (
          <button
            type="button"
            onClick={() => onPreview(file)}
            className="file-preview-button btn-icon"
            title={__('Prévisualiser')}
            aria-label={__('Prévisualiser')}
          >
            <img src={viewIconSm} alt="" className="file-item__icon" />
          </button>
        )}
        {showDownload && onDownload && (
          <button
            type="button"
            onClick={() => onDownload(file)}
            className="file-download-button btn-icon"
            title={__('Télécharger')}
            aria-label={__('Télécharger')}
          >
            <img src={downloadIconSm} alt="" className="file-item__icon" />
          </button>
        )}
        {showDelete && onDelete && (
          <button
            type="button"
            onClick={() => onDelete(file.id)}
            className="file-delete-button btn-icon"
            title={deleteLabel ?? __('Supprimer')}
            aria-label={deleteLabel ?? __('Supprimer')}
            disabled={isDeleting}
          >
            <img src={deleteIconSm} alt="" className="file-item__icon" />
          </button>
        )}
      </div>
    </li>
  );
};

export default FileListItem;

